import { Button, Card, makeStyles, tokens } from '@fluentui/react-components';
import {
    ArrowRightRegular,
    BriefcaseRegular,
    BuildingRegular,
    LocationRegular,
    PersonRegular,
    SettingsRegular,
} from '@fluentui/react-icons';
import { useAtom } from 'jotai';
import { get } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClientType } from '../../api';
import { ClientOnboardingFormAtom, FormState } from '../../store/ClientOnboardingFormAtom';
import { includesMpm, isMpmEnabled } from '../../utils/features';
import StickyActionBar from '../common/StickyActionBar';
import TopBar from '../common/TopBar';
import AddressPhoneCard from '../form/Pages/AddressPhoneCard';
import AdminCard from '../form/Pages/AdminCard';
import MpmCard from '../form/Pages/MpmCard';
import ProfileCard from '../form/Pages/ProfileCard';
import SectionRail, { SectionRailItem } from '../form/singlepage/SectionRail';
import SectionShell from '../form/singlepage/SectionShell';
import { wizardSteps } from '../form/wizard/wizardStepConfig';
import Container from '../layout/Container';

// ATA design standard §6.2 brand focus: brand-600 border + brand ring.
const BRAND_600 = '#1A4E8F';
const BRAND_FOCUS_RING = 'rgba(16, 58, 110, 0.28)';

const useStyles = makeStyles({
    // §4 — cap the form content like the prototype's .wiz-app (1080px, centered, 28px padding).
    page: {
        maxWidth: '1080px',
        margin: '0 auto',
        padding: '0 28px',
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: '220px 1fr',
        gap: '24px',
        alignItems: 'start',
        '@media (max-width: 960px)': {
            gridTemplateColumns: '1fr',
        },
    },
    card: {
        padding: 0,
        overflow: 'hidden',
    },
    // Shared control + label fidelity overrides for every field on the form (§3, §6.2, §6.3, §6.7).
    formControls: {
        // Field labels — 13px / 600, tight 6px gap to the control.
        '& .fui-Field__label': {
            fontSize: '13px',
            fontWeight: tokens.fontWeightSemibold,
            color: tokens.colorNeutralForeground1,
            marginBottom: '6px',
            lineHeight: '1.2',
        },
        // Control boxes: Input / Dropdown / Combobox — 38px, 7px radius, flat 1px stroke.
        '& .fui-Input, & .fui-Dropdown, & .fui-Combobox': {
            height: '38px',
            minHeight: '38px',
            borderRadius: tokens.borderRadiusMedium,
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            backgroundColor: tokens.colorNeutralBackground1,
        },
        // Drop Fluent's animated bottom focus accent; we use a full border + ring instead.
        '& .fui-Input::after, & .fui-Dropdown::after, & .fui-Combobox::after': {
            display: 'none',
        },
        '& .fui-Input:hover, & .fui-Dropdown:hover, & .fui-Combobox:hover': {
            border: `1px solid ${tokens.colorNeutralForeground3}`,
        },
        '& .fui-Input:focus-within, & .fui-Dropdown:focus-within, & .fui-Combobox:focus-within': {
            border: `1px solid ${BRAND_600}`,
            boxShadow: `0 0 0 2px ${BRAND_FOCUS_RING}`,
        },
        '& input': {
            fontSize: '14px',
        },
        '& input::placeholder': {
            color: tokens.colorNeutralForeground3,
        },
        // Sub-divider rule fills the row (Stack defaults to flex-start, which would shrink it),
        // and its label reads as an 11–12px/700 uppercase tertiary eyebrow (§6.8).
        '& .fui-Divider': {
            width: '100%',
        },
        '& .fui-Divider__wrapper': {
            fontSize: '12px',
            fontWeight: tokens.fontWeightBold,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: tokens.colorNeutralForeground3,
        },
    },
});

const sectionAnchorId = (key: string) => `section-${key}`;

const sectionIcon = (key: string, clientType: ClientType): ReactNode => {
    const isPerson = clientType === ClientType.Individual || clientType === ClientType.Staff;
    switch (key) {
        case 'profile':
            return isPerson ? <PersonRegular /> : <BuildingRegular />;
        case 'contact':
            return <LocationRegular />;
        case 'admin':
            return <SettingsRegular />;
        case 'projects':
            return <BriefcaseRegular />;
        default:
            return null;
    }
};

const hasValue = (v: unknown) => v !== undefined && v !== null && String(v).trim() !== '';

const FormPage = () => {
    const styles = useStyles();
    const [clientOnboardingForm, setClientOnboardingForm] = useAtom(ClientOnboardingFormAtom);
    const navigate = useNavigate();
    const location = useLocation();
    const editSection = (location.state as { editSection?: string } | null)?.editSection;

    const methods = useForm<FormState>({
        defaultValues: clientOnboardingForm,
        mode: 'onBlur',
    });

    // Subscribe to all values so section completion + visibility re-derive on change.
    const values = methods.watch();
    const clientType = (values.clientType ?? ClientType.Individual) as ClientType;

    const visibleSteps = useMemo(
        () => wizardSteps.filter((step) => !step.isVisible || step.isVisible(values)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [values.systems, values.clientType],
    );

    const [activeKey, setActiveKey] = useState(visibleSteps[0]?.key ?? 'profile');

    const sectionComplete = (key: string): boolean => {
        // ModernPM clients require at least one project (the section only shows when MPM is on).
        if (key === 'projects') {
            return (values.mpmSection?.projects?.length ?? 0) > 0;
        }
        const step = wizardSteps.find((s) => s.key === key);
        if (!step) return false;
        const fields = step.fieldsToValidate(values);
        return fields.every((path) => hasValue(get(values, path)));
    };

    const railItems: SectionRailItem[] = visibleSteps.map((step) => ({
        key: step.key,
        label: step.label,
        complete: sectionComplete(step.key),
    }));

    const scrollToSection = (key: string) => {
        const el = document.getElementById(sectionAnchorId(key));
        // Optional-call: scrollIntoView is unimplemented in jsdom (unit tests).
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        setActiveKey(key);
    };

    // Jump to a section when arriving from a Review "Edit" link.
    useEffect(() => {
        if (editSection) {
            // Defer so the section is mounted before scrolling.
            requestAnimationFrame(() => scrollToSection(editSection));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Scroll-spy: highlight the section currently in view.
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) {
                    setActiveKey(visible[0].target.id.replace('section-', ''));
                }
            },
            { rootMargin: '-20% 0px -70% 0px' },
        );
        visibleSteps.forEach((step) => {
            const el = document.getElementById(sectionAnchorId(step.key));
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [visibleSteps]);

    const renderSectionBody = (key: string): ReactNode => {
        switch (key) {
            case 'profile':
                return <ProfileCard />;
            case 'contact':
                return <AddressPhoneCard clientType={clientType} />;
            case 'admin':
                return <AdminCard clientType={clientType} />;
            case 'projects':
                return <MpmCard />;
            default:
                return null;
        }
    };

    const handleReviewSubmit = async () => {
        const allFields = visibleSteps.flatMap((step) => step.fieldsToValidate(values));
        const fieldsValid = allFields.length === 0 ? true : await methods.trigger(allFields as never);
        // Catches required-field gaps AND the "ModernPM needs ≥1 project" rule (via sectionComplete).
        const firstIncomplete = visibleSteps.find((step) => !sectionComplete(step.key));
        if (!fieldsValid || firstIncomplete) {
            if (firstIncomplete) scrollToSection(firstIncomplete.key);
            return;
        }
        setClientOnboardingForm(methods.getValues());
        navigate('/ReviewPage');
    };

    return (
        <Container>
            <div className={styles.page}>
                <TopBar />
                <FormProvider {...methods}>
                    <form className={styles.formControls} onSubmit={(e) => e.preventDefault()}>
                        <div className={styles.layout}>
                        <SectionRail items={railItems} activeKey={activeKey} onJump={scrollToSection} />
                        <Card className={styles.card}>
                            {visibleSteps.map((step) => (
                                <SectionShell
                                    key={step.key}
                                    id={sectionAnchorId(step.key)}
                                    icon={sectionIcon(step.key, clientType)}
                                    title={step.label}
                                    description={step.description}
                                >
                                    {renderSectionBody(step.key)}
                                </SectionShell>
                            ))}
                        </Card>
                    </div>
                </form>
            </FormProvider>
            <StickyActionBar
                label={`${visibleSteps.length} sections${
                    !isMpmEnabled() || includesMpm(values.systems) ? '' : ' · ModernPM off'
                }`}
            >
                <Button
                    appearance="primary"
                    size="large"
                    iconPosition="after"
                    icon={<ArrowRightRegular />}
                    onClick={handleReviewSubmit}
                >
                    Review &amp; Submit
                </Button>
            </StickyActionBar>
            {/* Spacer so the sticky action bar never overlaps the last section. */}
            <div style={{ height: 88 }} />
            </div>
        </Container>
    );
};

export default FormPage;
